import * as Sentry from '@sentry/nextjs';

import {
  getLogger,
  LogLevel,
  LogCategory,
  LogEvent,
  type LogMetadata,
} from '../../infrastructure/logger';

import { isCancellationError } from './isCancellationError';
import { isTransientError } from './isTransientError';
import { isUnauthorizedError } from './isUnauthorizedError';
import { sanitizeError } from './sanitizeError';

const logger = getLogger('ErrorReporting');

function getSentryContextRecord(
  sentryContext: Parameters<typeof Sentry.captureException>[1],
): Record<string, unknown> | null {
  if (
    sentryContext === undefined ||
    sentryContext === null ||
    typeof sentryContext !== 'object'
  ) {
    return null;
  }

  return sentryContext as Record<string, unknown>;
}

function getRecordProperty(
  record: Record<string, unknown>,
  key: string,
): Record<string, unknown> | null {
  const value = record[key];
  return value !== null && typeof value === 'object'
    ? (value as Record<string, unknown>)
    : null;
}

function getStringProperty(
  record: Record<string, unknown> | null,
  key: string,
): string | null {
  const value = record?.[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function getSentryLogMetadata(
  sentryContext: Parameters<typeof Sentry.captureException>[1],
): Partial<LogMetadata> {
  const context = getSentryContextRecord(sentryContext);
  if (!context) {
    return {};
  }

  const tags = getRecordProperty(context, 'tags');
  const extra = getRecordProperty(context, 'extra');
  const requestId =
    getStringProperty(tags, 'requestId') ??
    getStringProperty(extra, 'requestId');
  const errorName =
    getStringProperty(tags, 'errorName') ??
    getStringProperty(extra, 'errorName');

  return {
    ...(requestId ? { correlationId: requestId, requestId } : {}),
    ...(errorName ? { errorName } : {}),
    ...(tags ? { sentryTags: tags } : {}),
    ...(extra ? { sentryExtra: extra } : {}),
  };
}

export function reportError(
  error: unknown,
  message = 'Unhandled error',
  sentryContext?: Parameters<typeof Sentry.captureException>[1],
): { id: string | null } {
  // Don't report cancellation errors - these are expected during navigation/unmount
  if (isCancellationError(error)) {
    logger.contextualLog(
      LogLevel.DEBUG,
      'Request cancelled (not reporting to Sentry)',
      undefined,
      {
        category: LogCategory.API,
        event: LogEvent.API_ERROR,
      },
    );
    return { id: null };
  }

  // 401 responses are expected when sessions expire or auth is missing — not bugs.
  if (isUnauthorizedError(error)) {
    logger.contextualLog(LogLevel.WARN, message, undefined, {
      category: LogCategory.API,
      event: LogEvent.API_ERROR,
      ...getSentryLogMetadata(sentryContext),
      error: error instanceof Error ? error : undefined,
    });
    return { id: null };
  }

  // Transient errors (network blips, 502, 503, timeouts) are infrastructure noise,
  // not bugs. Log as WARN for dashboards but do not create Sentry issues.
  if (isTransientError(error)) {
    logger.contextualLog(LogLevel.WARN, message, undefined, {
      category: LogCategory.API,
      event: LogEvent.API_ERROR,
      ...getSentryLogMetadata(sentryContext),
      error: error instanceof Error ? error : undefined,
    });
    return { id: null };
  }

  // Convert error to a proper Error object for logging
  let errorObject: Error;
  let serializedError: string | Error;

  if (error instanceof Error) {
    errorObject = error;
    serializedError = error;
  } else if (typeof error === 'object' && error !== null) {
    try {
      // Sanitize error before serializing to prevent exposure of sensitive data
      const sanitized = sanitizeError(error);
      serializedError = JSON.stringify(sanitized);
      errorObject = new Error(serializedError);
    } catch {
      serializedError = '[Circular or non-serializable object]';
      errorObject = new Error(serializedError);
    }
  } else {
    // Primitive types (string, number, boolean, undefined, null, symbol, bigint)
    serializedError = String(error as string | number | boolean);
    errorObject = new Error(serializedError);
  }

  logger.contextualLog(LogLevel.ERROR, message, undefined, {
    category: LogCategory.API,
    event: LogEvent.API_ERROR,
    error: errorObject,
    // Preserve original error for debugging
    originalError: serializedError,
  });
  const id = Sentry.captureException(error, sentryContext);

  return { id };
}
